import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Headline,
  NEWS_CATEGORIES,
  fetchHeadlines,
  relativeTime,
} from './news';

const MAX_STORIES = 6;

// Shown instead of a thumbnail when a feed item carries no image.
const ImageFallback = ({ source }: { source: string }) => (
  <Box
    sx={{
      width: '100%',
      aspectRatio: '16 / 9',
      borderRadius: 1.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'action.hover',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="caption" color="text.secondary" fontWeight={700}>
      {source}
    </Typography>
  </Box>
);

const NewsPanel = () => {
  const [items, setItems] = useState<Headline[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [category, setCategory] = useState<string>('all');

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      setItems(await fetchHeadlines());
    } catch (err) {
      console.error(err);
      setItems([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    if (!items) return [];
    const matching =
      category === 'all'
        ? items
        : items.filter((item) => item.category === category);
    return matching.slice(0, MAX_STORIES);
  }, [items, category]);

  // Only offer a tab that actually has stories behind it.
  const categories = useMemo(() => {
    if (!items) return NEWS_CATEGORIES;
    return NEWS_CATEGORIES.filter(
      (entry) =>
        entry.value === 'all' ||
        items.some((item) => item.category === entry.value)
    );
  }, [items]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography variant="h6" color="primary">
          News
        </Typography>

        <Tooltip title="Refresh headlines">
          <span>
            <IconButton size="small" onClick={load} disabled={refreshing}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {items && items.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            mb: 2,
            pb: 1.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            overflowX: 'auto',
            // Let the row scroll on a phone without a visible scrollbar.
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {categories.map((entry) => (
            <Chip
              key={entry.value}
              label={entry.label}
              size="small"
              onClick={() => setCategory(entry.value)}
              variant={category === entry.value ? 'filled' : 'outlined'}
              sx={{
                flexShrink: 0,
                fontWeight: 800,
                fontSize: 11,
                letterSpacing: 0.4,
                ...(category === entry.value && {
                  bgcolor: 'rgba(255,47,146,0.18)',
                  borderColor: 'rgba(255,47,146,0.55)',
                  border: '1px solid',
                }),
              }}
            />
          ))}
        </Box>
      )}

      {!items ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress size={24} />
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          Headlines are unavailable right now.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2.25,
          }}
        >
          {shown.map((item) => (
            <Link
              key={item.link}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                minWidth: 0,
                '&:hover .headline': { color: 'primary.main' },
              }}
            >
              {item.image ? (
                <Box
                  component="img"
                  src={item.image}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: '100%',
                    aspectRatio: '16 / 9',
                    objectFit: 'cover',
                    borderRadius: 1.5,
                    display: 'block',
                    bgcolor: 'action.hover',
                  }}
                />
              ) : (
                <ImageFallback source={item.source} />
              )}

              <Typography
                className="headline"
                color="text.primary"
                sx={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  lineHeight: 1.32,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  transition: 'color 0.15s ease',
                }}
              >
                {item.title}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {item.source}
                {relativeTime(item.publishedAt)
                  ? ` · ${relativeTime(item.publishedAt)}`
                  : ''}
              </Typography>
            </Link>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default NewsPanel;
